import {
  Entity,
  selectCompaniesAndTransform,
  selectPersonsAndTransform,
  selectCompanyUnitsAndTransform,
  selectContainerClustersAndTransform,
  selectContainersAndTransform,
  selectFreightTypesAndTransform,
  selectMetersAndTransform,
  selectOperatingAreasAndTransform,
  selectPipelinesAndTransform,
  selectRegionsAndTransform,
  selectRouteGroupsAndTransform,
  selectRoutesAndTransform,
  selectServiceTypesAndTransform,
  selectSitesAndTransform,
  selectTrailersAndTransform,
  selectVehiclesAndTransform,
  selectWellsAndTransform,
  useGetAllCompaniesQuery,
  useGetAllPersonsQuery,
  useGetAllCompanyUnitsQuery,
  useGetAllContainerClustersQuery,
  useGetAllContainersQuery,
  useGetAllFreightTypesQuery,
  useGetAllMetersQuery,
  useGetAllOperatingAreasQuery,
  useGetAllPipelinesQuery,
  useGetAllRegionsQuery,
  useGetAllRouteGroupsQuery,
  useGetAllRoutesQuery,
  useGetAllServiceTypesQuery,
  useGetAllSitesQuery,
  useGetAllTrailersQuery,
  useGetAllVehiclesQuery,
  useGetAllWellsQuery,
} from '@ots-frontend/ots-data';
import { useMemo } from 'react';
import { useApp } from '../app/AppContext';
import { skipToken } from '@reduxjs/toolkit/query';
import { OtsCombobox } from '../components/OtsCombobox';
import { OtsFormFieldSize } from '../components/OtsFormField';
import { OtsMultiSelect } from '../components/OtsMultiSelect';
import type { OtsSelectOption } from '../components/OtsSelect';

export type OtsEntityMultiSelectProps = {
  id?: string;
  entityType: string;
  regionId?: string;
  entities?: Entity[];
  options?: OtsSelectOption[];
  /** Labels for saved selections that are missing from a current, filtered, or still-loading query. */
  selectedOptions?: OtsSelectOption[];
  label: string;
  description?: string;
  placeholder?: string;
  value: string[];
  onChange: (value: string[]) => void;
  onEntityChange?: (entities: Entity[]) => void;
  disabled?: boolean;
  size?: OtsFormFieldSize;
  selectionMode?: 'multiple' | 'single';
};

export type OtsEntitySelectProps = Omit<
  OtsEntityMultiSelectProps,
  'onChange' | 'onEntityChange' | 'selectionMode' | 'value'
> & {
  value?: string;
  onChange: (value: string | undefined) => void;
  onEntityChange?: (entity: Entity | undefined) => void;
};

type EntitySourceProps = Omit<
  OtsEntityMultiSelectProps,
  'entityType' | 'regionId' | 'entities' | 'options'
> & {
  regionId?: string;
};

type EntityOptionsProps = Omit<EntitySourceProps, 'regionId'> & {
  entities?: Entity[];
  options?: OtsSelectOption[];
  loading?: boolean;
  error?: boolean;
};

const EMPTY_ENTITIES: Entity[] = [];

const EntityOptions = ({
  entities = EMPTY_ENTITIES,
  options,
  selectedOptions,
  loading,
  error,
  selectionMode = 'multiple',
  onChange,
  onEntityChange,
  label,
  id,
  description,
  placeholder,
  value,
  disabled,
  size = 'default',
}: EntityOptionsProps) => {
  const current = useMemo(
    () =>
      options ??
      entities
        .map((entity) => ({ value: entity.id, label: entity.displayName }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [options, entities]
  );
  const currentIds = useMemo(
    () => new Set(current.map((option) => option.value)),
    [current]
  );
  const items = useMemo(() => {
    // Reuse the full catalog when live results already include the saved selection.
    const missing = (selectedOptions ?? []).filter(
      (option) => value.includes(option.value) && !currentIds.has(option.value)
    );
    return missing.length
      ? [
          ...new Map(missing.map((option) => [option.value, option])).values(),
          ...current,
        ]
      : current;
  }, [current, currentIds, selectedOptions, value]);
  const handleChange = (ids: string[]) => {
    onChange(ids);
    onEntityChange?.(entities.filter((entity) => ids.includes(entity.id)));
  };
  if (selectionMode === 'single') {
    return (
      <OtsCombobox
        id={id}
        label={label}
        description={description}
        placeholder={placeholder}
        options={items}
        value={value[0]}
        onChange={(selected) => handleChange(selected ? [selected] : [])}
        loading={loading}
        error={error ? 'Unable to load options.' : undefined}
        disabled={disabled}
        size={size}
      />
    );
  }
  return (
    <OtsMultiSelect
      id={id}
      label={label}
      description={description}
      placeholder={placeholder}
      options={items}
      value={value}
      onChange={handleChange}
      loading={loading}
      error={error ? 'Unable to load options.' : undefined}
      disabled={disabled}
      size={size}
      emptyMessage="No matching records are available."
    />
  );
};

const SiteSource = (props: EntitySourceProps) => {
  const query = useGetAllSitesQuery(props.regionId);
  const entities = useMemo(
    () => selectSitesAndTransform(query.data, { regionId: props.regionId }),
    [query.data, props.regionId]
  );
  return (
    <EntityOptions
      {...props}
      entities={entities}
      loading={query.isLoading}
      error={query.isError}
    />
  );
};

const RouteSource = (props: EntitySourceProps) => {
  const query = useGetAllRoutesQuery(props.regionId);
  const entities = useMemo(
    () => selectRoutesAndTransform(query.data, { regionId: props.regionId }),
    [query.data, props.regionId]
  );
  return (
    <EntityOptions
      {...props}
      entities={entities}
      loading={query.isLoading}
      error={query.isError}
    />
  );
};

const PersonSource = (props: EntitySourceProps) => {
  const query = useGetAllPersonsQuery(props.regionId);
  const entities = useMemo(
    () => selectPersonsAndTransform(query.data, { regionId: props.regionId }),
    [query.data, props.regionId]
  );
  return (
    <EntityOptions
      {...props}
      entities={entities}
      loading={query.isLoading}
      error={query.isError}
    />
  );
};

const VehicleSource = (props: EntitySourceProps) => {
  const query = useGetAllVehiclesQuery(props.regionId);
  const entities = useMemo(
    () => selectVehiclesAndTransform(query.data, { regionId: props.regionId }),
    [query.data, props.regionId]
  );
  return (
    <EntityOptions
      {...props}
      entities={entities}
      loading={query.isLoading}
      error={query.isError}
    />
  );
};

const TrailerSource = (props: EntitySourceProps) => {
  const query = useGetAllTrailersQuery(props.regionId ?? skipToken);
  const entities = useMemo(
    () => selectTrailersAndTransform(query.data, { regionId: props.regionId }),
    [query.data, props.regionId]
  );
  return (
    <EntityOptions
      {...props}
      entities={entities}
      loading={query.isLoading}
      error={query.isError}
    />
  );
};

const CompanySource = (props: EntitySourceProps) => {
  const query = useGetAllCompaniesQuery(props.regionId);
  const entities = useMemo(
    () => selectCompaniesAndTransform(query.data, { regionId: props.regionId }),
    [query.data, props.regionId]
  );
  return (
    <EntityOptions
      {...props}
      entities={entities}
      loading={query.isLoading}
      error={query.isError}
    />
  );
};

const CompanyUnitSource = (props: EntitySourceProps) => {
  const query = useGetAllCompanyUnitsQuery(props.regionId);
  const entities = useMemo(
    () =>
      selectCompanyUnitsAndTransform(query.data, { regionId: props.regionId }),
    [query.data, props.regionId]
  );
  return (
    <EntityOptions
      {...props}
      entities={entities}
      loading={query.isLoading}
      error={query.isError}
    />
  );
};

const WellSource = (props: EntitySourceProps) => {
  const query = useGetAllWellsQuery(props.regionId);
  const entities = useMemo(
    () => selectWellsAndTransform(query.data, { regionId: props.regionId }),
    [query.data, props.regionId]
  );
  return (
    <EntityOptions
      {...props}
      entities={entities}
      loading={query.isLoading}
      error={query.isError}
    />
  );
};

const OperatingAreaSource = (props: EntitySourceProps) => {
  const query = useGetAllOperatingAreasQuery(props.regionId);
  const entities = useMemo(
    () =>
      selectOperatingAreasAndTransform(query.data, {
        regionId: props.regionId,
      }),
    [query.data, props.regionId]
  );
  return (
    <EntityOptions
      {...props}
      entities={entities}
      loading={query.isLoading}
      error={query.isError}
    />
  );
};

const RouteGroupSource = (props: EntitySourceProps) => {
  const query = useGetAllRouteGroupsQuery(props.regionId);
  const entities = useMemo(
    () =>
      selectRouteGroupsAndTransform(query.data, { regionId: props.regionId }),
    [query.data, props.regionId]
  );
  return (
    <EntityOptions
      {...props}
      entities={entities}
      loading={query.isLoading}
      error={query.isError}
    />
  );
};

const MeterSource = (props: EntitySourceProps) => {
  const query = useGetAllMetersQuery(props.regionId);
  const entities = useMemo(
    () => selectMetersAndTransform(query.data, { regionId: props.regionId }),
    [query.data, props.regionId]
  );
  return (
    <EntityOptions
      {...props}
      entities={entities}
      loading={query.isLoading}
      error={query.isError}
    />
  );
};

const FreightTypeSource = (props: EntitySourceProps) => {
  const query = useGetAllFreightTypesQuery(props.regionId);
  const entities = useMemo(
    () =>
      selectFreightTypesAndTransform(query.data, { regionId: props.regionId }),
    [query.data, props.regionId]
  );
  return (
    <EntityOptions
      {...props}
      entities={entities}
      loading={query.isLoading}
      error={query.isError}
    />
  );
};

const ServiceTypeSource = (props: EntitySourceProps) => {
  const query = useGetAllServiceTypesQuery(props.regionId);
  const entities = useMemo(
    () =>
      selectServiceTypesAndTransform(query.data, { regionId: props.regionId }),
    [query.data, props.regionId]
  );
  return (
    <EntityOptions
      {...props}
      entities={entities}
      loading={query.isLoading}
      error={query.isError}
    />
  );
};

const RegionSource = (props: EntitySourceProps) => {
  const query = useGetAllRegionsQuery();
  const entities = useMemo(
    () => selectRegionsAndTransform(query.data),
    [query.data]
  );
  return (
    <EntityOptions
      {...props}
      entities={entities}
      loading={query.isLoading}
      error={query.isError}
    />
  );
};

const ContainerSource = (props: EntitySourceProps) => {
  const query = useGetAllContainersQuery(props.regionId);
  const entities = useMemo(
    () =>
      selectContainersAndTransform(query.data, { regionId: props.regionId }),
    [query.data, props.regionId]
  );
  return (
    <EntityOptions
      {...props}
      entities={entities}
      loading={query.isLoading}
      error={query.isError}
    />
  );
};

const PipelineSource = (props: EntitySourceProps) => {
  const query = useGetAllPipelinesQuery(props.regionId);
  const entities = useMemo(
    () => selectPipelinesAndTransform(query.data, { regionId: props.regionId }),
    [query.data, props.regionId]
  );
  return (
    <EntityOptions
      {...props}
      entities={entities}
      loading={query.isLoading}
      error={query.isError}
    />
  );
};

const ContainerClusterSource = (props: EntitySourceProps) => {
  const query = useGetAllContainerClustersQuery(props.regionId);
  const entities = useMemo(
    () =>
      selectContainerClustersAndTransform(query.data, {
        regionId: props.regionId,
      }),
    [query.data, props.regionId]
  );
  return (
    <EntityOptions
      {...props}
      entities={entities}
      loading={query.isLoading}
      error={query.isError}
    />
  );
};

const ConnectedEntityMultiSelect = ({
  entityType,
  ...props
}: Omit<OtsEntityMultiSelectProps, 'entities' | 'options'>) => {
  switch (entityType.toUpperCase()) {
    case 'SITE':
      return <SiteSource {...props} />;
    case 'ROUTE':
      return <RouteSource {...props} />;
    case 'PERSON':
      return <PersonSource {...props} />;
    case 'VEHICLE':
      return <VehicleSource {...props} />;
    case 'TRAILER':
      return <TrailerSource {...props} />;
    case 'COMPANY':
      return <CompanySource {...props} />;
    case 'COMPANYUNIT':
    case 'COMPANY_UNIT':
      return <CompanyUnitSource {...props} />;
    case 'WELL':
      return <WellSource {...props} />;
    case 'OPERATINGAREA':
    case 'OPERATING_AREA':
      return <OperatingAreaSource {...props} />;
    case 'ROUTEGROUP':
    case 'ROUTE_GROUP':
      return <RouteGroupSource {...props} />;
    case 'METER':
      return <MeterSource {...props} />;
    case 'FREIGHTTYPE':
    case 'FREIGHT_TYPE':
      return <FreightTypeSource {...props} />;
    case 'SERVICETYPE':
    case 'SERVICE_TYPE':
      return <ServiceTypeSource {...props} />;
    case 'REGION':
      return <RegionSource {...props} />;
    case 'CONTAINER':
      return <ContainerSource {...props} />;
    case 'PIPE':
    case 'PIPELINE':
      return <PipelineSource {...props} />;
    case 'CONCOMMIGLED':
    case 'CONTAINERCLUSTER':
    case 'CONTAINER_CLUSTER':
      return <ContainerClusterSource {...props} />;
    default:
      return <EntityOptions {...props} error />;
  }
};

/** Tailwind-native entity selector backed by the existing RTK Query entity stores. */
export const OtsEntityMultiSelect = ({
  entityType,
  regionId,
  entities,
  options,
  ...props
}: OtsEntityMultiSelectProps) => {
  const app = useApp();
  if (entities || options) {
    return <EntityOptions {...props} entities={entities} options={options} />;
  }
  return (
    <ConnectedEntityMultiSelect
      {...props}
      entityType={entityType}
      regionId={regionId ?? app?.regionId}
    />
  );
};

/** Tailwind-native single entity selector using the same query-backed sources. */
export const OtsEntitySelect = ({
  value,
  onChange,
  onEntityChange,
  ...props
}: OtsEntitySelectProps) => (
  <OtsEntityMultiSelect
    {...props}
    selectionMode="single"
    value={value ? [value] : []}
    onChange={(ids) => onChange(ids[0])}
    onEntityChange={(entities) => onEntityChange?.(entities[0])}
  />
);
